import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { api } from "@/lib/api";

const ShopCtx = createContext(null);
const load = (k, d) => {
  try {
    return JSON.parse(localStorage.getItem(k)) ?? d;
  } catch (e) {
    return d;
  }
};

export function lineMax(p, mode, variant) {
  if (mode === "ready") {
    const v = (p.variants || []).find((x) => x.name === variant);
    return v ? v.stock : p.stock || 0;
  }
  const po = p.po_info || {};
  const caps = [99, po.max_per_order, po.quota_left].filter((x) => x !== null && x !== undefined && x !== 0);
  return po.quota_left === 0 ? 0 : Math.min(...caps);
}

export function ShopProvider({ children }) {
  const [cart, setCart] = useState(() => load("bk_cart", []));
  const [favs, setFavs] = useState(() => load("bk_favs", []));
  useEffect(() => {
    localStorage.setItem("bk_cart", JSON.stringify(cart));
  }, [cart]);
  useEffect(() => {
    localStorage.setItem("bk_favs", JSON.stringify(favs));
  }, [favs]);

  const addToCart = useCallback((p, mode, variant = "", qty = 1) => {
    const key = `${p.id}|${variant}|${mode}`;
    const max = lineMax(p, mode, variant);
    let ok = true;
    setCart((c) => {
      const ex = c.find((l) => l.key === key);
      const next = (ex?.qty || 0) + qty;
      if (next > max) {
        ok = false;
        return c;
      }
      if (ex) return c.map((l) => (l.key === key ? { ...l, qty: next } : l));
      return [...c, { key, product_id: p.id, slug: p.slug, name: p.name, image: p.images?.[0], variant, mode, qty }];
    });
    if (ok) toast.success(`${p.name} ditambahkan ke keranjang`);
    else toast.error(`Jumlah melebihi batas (${max})`);
  }, []);

  const value = useMemo(
    () => ({
      cart,
      favs,
      addToCart,
      setQty: (key, qty) => setCart((c) => c.map((l) => (l.key === key ? { ...l, qty } : l))),
      removeLine: (key) => setCart((c) => c.filter((l) => l.key !== key)),
      clearCart: () => setCart([]),
      isFav: (id) => favs.includes(id),
      toggleFav: (id) => setFavs((f) => (f.includes(id) ? f.filter((x) => x !== id) : [id, ...f])),
      cartCount: cart.reduce((s, l) => s + l.qty, 0),
    }),
    [cart, favs, addToCart]
  );
  return <ShopCtx.Provider value={value}>{children}</ShopCtx.Provider>;
}

export const useShop = () => useContext(ShopCtx);

export function useSettings() {
  const { data } = useQuery({ queryKey: ["settings"], queryFn: () => api.get("/settings/public").then((r) => r.data), staleTime: 300000 });
  return data || { store_name: "Bengkel Toys & Hobbies", whatsapp: "6282352611452", instagram: "bengkelhobbyptk", hours: [], faq: [], po_terms: {} };
}

export function useSeo(title, description) {
  useEffect(() => {
    document.title = title ? `${title} | Bengkel Toys & Hobbies` : "Bengkel Toys & Hobbies — Your Hobby, Your Story.";
    if (description) {
      let m = document.querySelector('meta[name="description"]');
      if (m) m.setAttribute("content", description.slice(0, 160));
    }
  }, [title, description]);
}
